import React, { useEffect, useState } from 'react'
import { PostHog } from '@/services/posthog'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISSED_KEY = 'pwa_install_dismissed_until'
const VISIT_COUNT_KEY = 'pwa_visit_count'
const HIDE_FOREVER_KEY = 'pwa_install_hidden'

function isIOS() {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) &&
    !(window as unknown as { MSStream?: unknown }).MSStream
  )
}

function isInStandaloneMode() {
  return (
    'standalone' in window.navigator &&
    (window.navigator as { standalone?: boolean }).standalone === true
  )
}

const PWAInstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [show, setShow] = useState(false)
  const [showIOS, setShowIOS] = useState(false)

  useEffect(() => {
    if (localStorage.getItem(HIDE_FOREVER_KEY)) return
    if (isInStandaloneMode()) return

    // Track visit count
    const count = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10) + 1
    localStorage.setItem(VISIT_COUNT_KEY, String(count))

    if (count < 2) return // show only from 2nd visit

    // Check dismiss cooldown
    const dismissedUntil = parseInt(localStorage.getItem(DISMISSED_KEY) || '0', 10)
    if (Date.now() < dismissedUntil) return

    if (isIOS() && !isInStandaloneMode()) {
      setShowIOS(true)
      return
    }

    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setShow(true)
    }

    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  const handleInstall = async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') {
      localStorage.setItem(HIDE_FOREVER_KEY, '1')
      PostHog.pwaInstalled()
    }
    setShow(false)
    setDeferredPrompt(null)
  }

  const handleLater = () => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000))
    setShow(false)
    setShowIOS(false)
  }

  if (!show && !showIOS) return null

  return (
    <>
      {/* Mobile banner (bottom slide-up) */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:hidden animate-in slide-in-from-bottom-4 duration-300">
        <div className="bg-card border rounded-2xl shadow-xl p-4 flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <img src="/icons/icon-96x96.png" alt="Octoveto" className="w-12 h-12 rounded-xl" />
            <div>
              <p className="font-semibold text-sm">Installer Octoveto</p>
              <p className="text-xs text-muted-foreground">Accès rapide · Mode hors ligne · Notifications</p>
            </div>
          </div>
          {showIOS ? (
            <p className="text-xs text-muted-foreground">
              Sur Safari : appuyez sur <strong>Partager</strong> puis <strong>Sur l'écran d'accueil</strong>
            </p>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleInstall}
                className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold text-sm py-2 rounded-xl transition-colors"
              >
                Installer
              </button>
              <button
                onClick={handleLater}
                className="flex-1 border font-semibold text-sm py-2 rounded-xl text-muted-foreground hover:bg-muted transition-colors"
              >
                Plus tard
              </button>
            </div>
          )}
          {showIOS && (
            <button onClick={handleLater} className="text-xs text-muted-foreground underline self-end">
              Fermer
            </button>
          )}
        </div>
      </div>

      {/* Desktop card (top-right) */}
      <div className="fixed top-4 right-4 z-50 hidden md:block animate-in slide-in-from-top-4 duration-300">
        <div className="bg-card border rounded-2xl shadow-xl p-4 w-72 flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <img src="/icons/icon-96x96.png" alt="Octoveto" className="w-10 h-10 rounded-xl" />
            <div>
              <p className="font-semibold text-sm">Installer Octoveto</p>
              <p className="text-xs text-muted-foreground">Accès rapide · Mode hors ligne</p>
            </div>
          </div>
          {showIOS ? (
            <p className="text-xs text-muted-foreground">
              Sur Safari : appuyez sur <strong>Partager</strong> puis <strong>Sur l'écran d'accueil</strong>
            </p>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleInstall}
                className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold text-sm py-2 rounded-xl transition-colors"
              >
                Installer
              </button>
              <button
                onClick={handleLater}
                className="border font-semibold text-sm py-2 px-3 rounded-xl text-muted-foreground hover:bg-muted transition-colors"
              >
                Plus tard
              </button>
            </div>
          )}
          {showIOS && (
            <button onClick={handleLater} className="text-xs text-muted-foreground underline self-end">
              Fermer
            </button>
          )}
        </div>
      </div>
    </>
  )
}

export default PWAInstallPrompt
